import { useCallback, useMemo, useRef } from 'react';
import { startTransition } from 'react';

interface UpdateFunction<T> {
  (rowIndex: number, columnId: string, value: string | boolean): void;
}

export const useDebouncedUpdate = <T>(
  setData: React.Dispatch<React.SetStateAction<T[]>>,
  debounceMs = 50,
): UpdateFunction<T> => {
  const timeoutRef = useRef<number | undefined>();
  const pendingUpdatesRef = useRef<
    Array<{
      rowIndex: number;
      columnId: string;
      value: string;
    }>
  >([]);

  const flushUpdates = useCallback(() => {
    if (pendingUpdatesRef.current.length === 0) return;

    const updates = [...pendingUpdatesRef.current];
    pendingUpdatesRef.current = [];

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    setData((previousRows) => {
      const nextRows = [...previousRows];
      updates.forEach(({ rowIndex, columnId, value }) => {
        const targetRow = nextRows[rowIndex];
        if (targetRow) {
          // Convert string back to boolean if the original value was boolean
          const convertedValue = value === 'true' || value === 'false' ? value === 'true' : value;
          nextRows[rowIndex] = { ...targetRow, [columnId]: convertedValue } as T;
        }
      });
      return nextRows;
    });
  }, [setData]);

  const debouncedUpdate = useCallback(
    (rowIndex: number, columnId: string, value: string | boolean) => {
      // Convert boolean to string for storage in pending updates
      const stringValue = typeof value === 'boolean' ? String(value) : value;

      // Add to pending updates
      pendingUpdatesRef.current.push({ rowIndex, columnId, value: stringValue });

      // Clear existing timeout
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = window.setTimeout(flushUpdates, debounceMs);
    },
    [setData, debounceMs, flushUpdates],
  );

  // Immediate update function for toggle switches that need instant feedback
  const immediateUpdate = useCallback(
    (rowIndex: number, columnId: string, value: string | boolean) => {
      // Clear pending updates for immediate execution
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      pendingUpdatesRef.current = [];

      // Use direct state update without startTransition for truly immediate updates
      setData((previousRows) => {
        const nextRows = [...previousRows];
        const targetRow = nextRows[rowIndex];
        if (targetRow) {
          nextRows[rowIndex] = { ...targetRow, [columnId]: value } as T;
        }
        return nextRows;
      });
    },
    [setData],
  );

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  return useMemo(() => {
    const updateFn = debouncedUpdate as UpdateFunction<T> & {
      cleanup: () => void;
      immediate: typeof immediateUpdate;
      flush: () => void;
    };
    updateFn.cleanup = cleanup;
    updateFn.immediate = immediateUpdate;
    updateFn.flush = flushUpdates;
    return updateFn;
  }, [debouncedUpdate, cleanup, immediateUpdate, flushUpdates]);
};
